'use client';

import { Toaster as SonnerToaster, toast as sonnerToast } from 'sonner';
import { cva, type VariantProps } from 'class-variance-authority';
import {
  CheckCircle,
  X,
  AlertCircle,
  InfoCircle,
  AlertTriangle
} from '@untitledui/icons';
import { JSX } from 'react';

const toastVariants = cva(
  'pointer-events-auto flex w-full max-w-md rounded-lg border p-4 shadow-lg ring-1 ring-black/5 transition-all duration-300 ease-in-out',
  {
    variants: {
      colorScheme: {
        info: 'border-blue-200 bg-blue-50 text-blue-800',
        success: 'border-green-200 bg-green-50 text-green-800',
        warning: 'border-yellow-200 bg-yellow-50 text-yellow-800',
        error: 'border-red-200 bg-red-50 text-red-800'
      }
    },
    defaultVariants: {
      colorScheme: 'info'
    }
  }
);

type ToastProps = {
  id: string | number;
  title: string;
  description?: string | React.ReactNode;
} & VariantProps<typeof toastVariants>;

const colorSchemeToIcon: Record<
  NonNullable<ToastProps['colorScheme']>,
  JSX.Element
> = {
  info: <InfoCircle className="h-5 w-5 text-blue-500" />,
  success: <CheckCircle className="h-5 w-5 text-green-500" />,
  warning: <AlertTriangle className="h-5 w-5 text-yellow-500" />,
  error: <AlertCircle className="h-5 w-5 text-red-500" />
};

export function Toast(props: ToastProps) {
  const { title, description, id, colorScheme = 'info' } = props;

  return (
    <div className={toastVariants({ colorScheme })}>
      <div className="mr-3 flex-shrink-0">
        {colorSchemeToIcon[colorScheme as keyof typeof colorSchemeToIcon]}
      </div>
      <div className="flex-1 pt-0.5">
        <h3 className="text-sm font-medium">{title}</h3>
        {description && (
          <div className="mt-1 text-sm opacity-90">{description}</div>
        )}
      </div>
      <div className="ml-4 flex flex-shrink-0">
        <button
          className="inline-flex rounded-md bg-transparent text-current hover:text-opacity-75 focus:outline-none focus:ring-2 focus:ring-offset-2"
          onClick={() => {
            sonnerToast.dismiss(id);
          }}
        >
          <span className="sr-only">Close</span>
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}

function toast(toast: Omit<ToastProps, 'id'>) {
  return sonnerToast.custom((id) => (
    <Toast
      id={id}
      title={toast.title}
      description={toast.description}
      colorScheme={toast.colorScheme}
    />
  ));
}

function Toaster() {
  return (
    <SonnerToaster
      offset={{
        right: '3rem',
        bottom: '3rem'
      }}
      mobileOffset={{
        bottom: '3rem'
      }}
      position="bottom-right"
    />
  );
}

const dismissToast = sonnerToast.dismiss;

export { Toaster, toast, dismissToast };
